import 'reflect-metadata';

var PopulateMode;
(function (PopulateMode) {
    PopulateMode["STRICT"] = "STRICT";
    PopulateMode["WARN"] = "WARN";
    PopulateMode["SILENT"] = "SILENT";
})(PopulateMode || (PopulateMode = {}));
class BaseModel {
    constructor(data = {}, mode = PopulateMode.SILENT) {
        this._values = {};
        Object.defineProperty(this, '_values', { enumerable: false });
        Object.keys(this._properties).forEach(propertyKey => {
            Object.defineProperty(this, propertyKey, {
                get: function () {
                    return this._properties[propertyKey].get(this);
                },
                set: function (value) {
                    this._properties[propertyKey].set(this, value);
                },
                enumerable: true,
                configurable: false,
            });
        });
        this.populate(data, mode);
    }
    get _properties() {
        return this.constructor['_properties'] || {};
    }
    isEmpty() {
        return Object.keys(this._values).length === 0;
    }
    /**
     * Use to map some data object values to this model object.
     * @param data the data object who's values will be mapped to the model
     * @param mode defaults to PopulateMode.STRICT which will make this process throw and error if the mapped data has
     * keys that are not present in the model. Use PopulateMode.WARN to only print a warning about missing keys to
     * the console or use PopulateMode.SILENT to just completely ignore extra keys.
     */
    populate(data, mode = PopulateMode.SILENT) {
        if (data && !(data instanceof Object)) {
            // TODO: create custom error
            throw new Error('populate requires an instance of Object, got "' + typeof data + '"');
        }
        Object.keys(data).forEach(key => {
            if (!(data instanceof BaseModel && data[key] === undefined)) {
                // eslint-disable-next-line no-prototype-builtins
                if (this._properties.hasOwnProperty(key)) {
                    const propertyType = this._properties[key];
                    propertyType.set(this, data[key], mode);
                }
                else if (!(data instanceof BaseModel && data[key] === undefined)) {
                    // TODO: create custom error
                    // NOTE: Switching this custom error off as it doesn't allow mapping
                    const name = this._getClassName();
                    if (mode === PopulateMode.STRICT) {
                        throw new Error(`Model "${name}" has no property "${key}".`);
                    }
                    else if (mode === PopulateMode.WARN) {
                        console.warn(`Model "${name}" has no property "${key}".`);
                    }
                    // PopulateMode.SILENT
                    // shhhh just let it go and accept it. Take a deep breath and move on.
                }
            }
        });
    }
    _getClassName() {
        let name;
        try {
            if (this.constructor.name) {
                name = this.constructor.name;
            }
            else {
                const funcNameRegex = /function\s+([^\s(]+)\s*\(/;
                const results = funcNameRegex.exec(this.constructor.toString());
                name = results && results.length > 1 ? results[1] : name;
            }
        }
        catch (err) {
            name = '*unknown model*';
        }
        return name;
    }
}

const PRIMITIVE_TYPES$1 = new Set([String, Number, Boolean, Object]);
class FieldType {
    constructor(propertyType, propertyKey, isRepeated = false, defaultValue, keyValue) {
        this._defaultValue = defaultValue;
        this._name = propertyKey;
        this._isRepeated = isRepeated;
        this._keyValue = keyValue;
        if (propertyType) {
            if (propertyType.constructor && propertyType.constructor !== Function) {
                // eslint-disable-next-line no-prototype-builtins
                if (propertyType.hasOwnProperty(0)) {
                    propertyType = Number;
                }
                else {
                    propertyType = String;
                }
            }
        }
        this._type = propertyType;
    }
    get isPrimitive() {
        return PRIMITIVE_TYPES$1.has(this._type);
    }
    get isRepeated() {
        return this._isRepeated;
    }
    get name() {
        return this._type.name;
    }
    get(instance) {
        const value = instance._values[this._name];
        if (value === undefined && this._defaultValue) {
            return this._defaultValue;
        }
        return value;
    }
    set(instance, value) {
        instance._values[this._name] = this.isPrimitive || value === null ? value : this._create(instance, value);
    }
    _create(instance, value) {
        if (this._isRepeated) {
            if (!Array.isArray(value)) {
                const constructorName = (this.constructor && this.constructor.name) || '';
                const error = `Field "${constructorName}.${this._name}" requires value to be an Array. Value was of type "${typeof value}".`;
                throw new Error(error);
            }
            return value.filter(item => item !== null).map(item => this._createValue(item));
        }
        else {
            return this._createValue(value);
        }
    }
    _createValue(value) {
        if (value instanceof this._type) {
            return value;
        }
        if (this._type === Map) {
            if (Array.isArray(this._keyValue)) {
                return this._constructMap(value, this._keyValue);
            }
            return new Map(Object.entries(value));
        }
        const args = [this._type, value];
        return new (this._type.bind(...args))();
    }
    _constructMap(value, keyValue) {
        let entries;
        if (Array.isArray(keyValue[1])) {
            entries = Object.entries(value).map(([key, value]) => {
                return [key, this._constructMap(value, keyValue[1])];
            });
        }
        else if (keyValue[1].prototype instanceof Model) {
            entries = Object.keys(value).reduce((arr, key) => {
                const args = [keyValue[1], value[key]];
                arr.push([key, new (keyValue[1].bind(...args))()]);
                return arr;
            }, []);
        }
        else {
            entries = Object.entries(value);
        }
        return new Map(entries);
    }
}
const Field = function Field(options = {}) {
    const { defaultValue, type, keyValue } = options;
    return function (target, propertyKey) {
        const reflected = Reflect.getMetadata('design:type', target, propertyKey);
        const isRepeated = type && reflected === Array;
        const propertyType = type ? type : reflected;
        const prop = new FieldType(propertyType, propertyKey, isRepeated, defaultValue, keyValue);
        if (type) {
            Reflect.defineMetadata('core:type', type, target, propertyKey);
        }
        if (keyValue) {
            Reflect.defineMetadata('core:keyValue', keyValue, target, propertyKey);
        }
        target.constructor['_properties'] = Object.assign({}, target.constructor['_properties'] || target['_properties']);
        target.constructor['_properties'][propertyKey] = prop;
    };
};

var ModelAnnotation;
(function (ModelAnnotation) {
    ModelAnnotation[ModelAnnotation["DATA"] = 0] = "DATA";
    ModelAnnotation[ModelAnnotation["VALUE"] = 1] = "VALUE";
})(ModelAnnotation || (ModelAnnotation = {}));
/**
 * Base class for Models.
 * Should always be used with either {@link Data}() or {@link Value}() decorators.
 *
 * The main point with this is to allow defining models with each field's type
 * can be accessed at runtime. For example to generate swagger definitions based on models.
 * This also gives developers type info about models in the constructor.
 *
 * The {@link Data}() decorator creates a model with both getters and setters.
 * The {@link Value}() decorator creates a model with getters, ie readonly.
 *
 * @example
 * ```typescript
 * @Data()
 * class Book extends Model<Book> {
 *   @Field() title: string;
 * }
 *
 * // When using subclassing for models generic type must be carried forward
 * @Data()
 * class Book<T extends Book<any>> extends Model<T> {
 *   @Field() title: string;
 * }
 *
 * @Data()
 * class Paperback extends Book<Paperback>  {
 *     @Field() store: string;
 * }
 *```
 *
 */
class Model {
    /**
     * Construct a new instance of a Model.
     *
     * Any class that extends Model<T> need to be decorated with either a @{@link Data}() (getters/setters)
     * or a @{@link Value}() (getters) decorator.
     *
     * @see Data
     * @see Value
     *
     * @template T
     * @param {ModelData<T>} data
     */
    constructor(data = {}) {
        this._values = {};
        this._validateAnnotation();
        Object.defineProperty(this, '_values', { enumerable: false });
        this._setData(data);
    }
    get _properties() {
        return this.constructor['_properties'];
    }
    _setData(data) {
        const values = this._getValues(data);
        Object.entries(values).forEach(([propertyKey, value]) => {
            if (this._properties[propertyKey] instanceof FieldType) {
                this._properties[propertyKey].set(this, value);
            }
        });
    }
    _getValues(data) {
        let values = {};
        if (data instanceof Model) {
            Object.entries(data._values).forEach(([property, value]) => {
                values[property] = value instanceof Model ? value._getValues(value._values) : value;
            });
        }
        else {
            values = data;
        }
        return values;
    }
    _validateAnnotation() {
        if (!(this.constructor['_annotation'] in ModelAnnotation)) {
            const constructorName = (this.constructor && this.constructor.name) || '';
            const error = `Model ${constructorName} is missing required @Data() or @Value() annotation.`.replace(/\s{2,}/, ' ');
            throw new Error(error);
        }
    }
}
Model._properties = {};
/**
 * Decorate a subclass of {@link Model} with getters and setters.
 *
 * @example
 * ```typescript
 * @Data()
 * class Book extends Model<Book> {
 *   @Field() title: string;
 * }
 *
 * const book = new Book({title: 'Across the nightingales floor'});
 * book.title = 'Grass for his pillow';
 * book.title // 'Grass for his pillow'
 * ```
 *
 */
const Data = function Data() {
    return function (constructorFunction) {
        constructorFunction['_annotation'] = ModelAnnotation.DATA;
        return class extends constructorFunction {
            constructor(...args) {
                super(...args);
                const properties = Object.keys(this._properties).filter(key => {
                    return !Object.prototype.hasOwnProperty.call(this, key);
                });
                properties.forEach(propertyKey => {
                    Object.defineProperty(this, propertyKey, {
                        get: function () {
                            return this._properties[propertyKey].get(this);
                        },
                        set: function (value) {
                            this._properties[propertyKey].set(this, value);
                        },
                        enumerable: true,
                        configurable: false,
                    });
                });
            }
        };
    };
};
/**
 * Decorate a subclass of {@link Model} with getters. Since only getters are created the models is immutable.
 *
 * @example
 * ```typescript
 * @Value()
 * class Book extends Model<Book> {
 *   @Field() title: string;
 * }
 *
 * const book = new Book({title: 'Across the nightingales floor'});
 * book.title = 'Grass for his pillow'; <-- Throws error "TypeError: Cannot set property title of #<Book> which has only a getter"
 * ```
 *
 */
const Value = function Value() {
    return function (constructorFunction) {
        constructorFunction['_annotation'] = ModelAnnotation.VALUE;
        return class extends constructorFunction {
            constructor(...args) {
                super(...args);
                const properties = Object.keys(this._properties).filter(key => {
                    return !Object.prototype.hasOwnProperty.call(this, key);
                });
                properties.forEach(propertyKey => {
                    Object.defineProperty(this, propertyKey, {
                        get: function () {
                            return this._properties[propertyKey].get(this);
                        },
                        enumerable: true,
                        configurable: false,
                    });
                });
            }
        };
    };
};

/**
 * A builder of crating new instances of model that as the {@link Value} decorator.
 *
 * A Builder instance creates a dynamic setter method for each property of a {@link Model} and a {@link Builder.build} method
 * in order to create a new instance.
 *
 * @example
 * ```typescript
 * @Value()
 * class Book extends Model<Book> {
 *   @Field() title: string;
 *   @Field() year: string;
 * }
 *
 * const book = new Book({title: 'Across the nightingales floor', year: '2021'});
 * const builder = Builder.from<Book>(book);
 * const newBook = builder.title('Grass for his pillow').build();
 * newBook.title // 'Grass for his pillow';
 * newBook.year // '2021';
 * ```
 */
class Builder {
    /**
     * Construct a new builder for building a new instance from an existing instance of a {@link Model}.
     * @template T
     * @param {Model<T>} instance
     */
    constructor(instance) {
        this._values = {};
        this._ctor = instance.constructor;
        this._values = Object.assign({}, this._getValues(instance));
        Object.entries(this._ctor['_properties']).forEach(([propertyKey]) => {
            this[propertyKey] = v => {
                this._values[propertyKey] = v;
                return this;
            };
        });
    }
    /**
     * Static method for creating a {@link Builder} from a {@link Model}.
     * @see Builder
     */
    static from(data) {
        return new Builder(data);
    }
    /**
     * Construct model from builder
     * @template T
     * @return {Model<T>} instance
     */
    build() {
        return new (this._ctor.bind(this._ctor, this._values))();
    }
    _getValues(instance) {
        return Object.entries(instance['_values']).reduce((values, [key, value]) => {
            values[key] = this._getValue(value);
            return values;
        }, {});
    }
    _getValue(value) {
        if (value instanceof Map) {
            const obj = {};
            for (const [key, v] of value) {
                obj[key.toString()] = this._getValue(v);
            }
            return obj;
        }
        else if (value instanceof Model) {
            return this._getValues(value);
        }
        return value;
    }
}

const PRIMITIVE_TYPES = new Set([String, Number, Boolean, Object]);
function definePropertyOnModel(target, propertyKey, property) {
    target.constructor['_properties'] = Object.assign({}, target.constructor['_properties'] || target['_properties']);
    target.constructor['_properties'][propertyKey] = property;
}
class PropertyType {
    constructor(propertyType, propertyKey, isRepeated, defaultValue, keyValue) {
        this._isRepeated = false;
        this._defaultValue = defaultValue;
        this._name = propertyKey;
        this._isRepeated = isRepeated;
        this._keyValue = keyValue;
        if (propertyType) {
            if (propertyType.constructor && propertyType.constructor !== Function) {
                // eslint-disable-next-line no-prototype-builtins
                if (propertyType.hasOwnProperty(0)) {
                    propertyType = Number;
                }
                else {
                    propertyType = String;
                }
            }
        }
        this._type = propertyType;
    }
    get isPrimitive() {
        return PRIMITIVE_TYPES.has(this._type);
    }
    get isRepeated() {
        return this._isRepeated;
    }
    get name() {
        return this._type.name;
    }
    get(instance) {
        // eslint-disable-next-line no-prototype-builtins
        if (!instance._values.hasOwnProperty(this._name)) {
            if (this._defaultValue) {
                return this._defaultValue;
            }
            return undefined;
        }
        return instance._values[this._name];
    }
    set(instance, value, mode = PopulateMode.STRICT) {
        instance._values[this._name] = this.isPrimitive || value === null ? value : this._create(instance, value, mode);
    }
    _create(instance, data, mode = PopulateMode.STRICT) {
        const current = this.get(instance);
        if (this._isRepeated) {
            if (!(data instanceof Array)) {
                // TODO: create custom error
                throw new Error('"' + this._name + '" is a Property, requires and Array. Got "' + typeof data + '".');
            }
            return data.filter(item => item !== null).map(item => this._createIfNoTypeMatch(item, mode));
        }
        else if (current instanceof BaseModel) {
            if (data instanceof BaseModel) {
                return data;
            }
            current.populate(data, mode);
            return current;
        }
        else {
            return this._createIfNoTypeMatch(data, mode);
        }
    }
    _createIfNoTypeMatch(d, mode) {
        if (!this._type) {
            return d;
        }
        if (d instanceof this._type) {
            return d;
        }
        if (this._type === Map) {
            if (Array.isArray(this._keyValue)) {
                return this._constructMap(d, this._keyValue);
            }
            return new Map(Object.entries(d));
        }
        const args = [this._type, d];
        if (this._type.prototype instanceof BaseModel) {
            args.push(mode);
        }
        return new (this._type.bind(...args))();
    }
    _constructMap(d, keyValue) {
        let entries;
        if (Array.isArray(keyValue[1])) {
            entries = Object.entries(d).map(([key, value]) => {
                return [key, this._constructMap(value, keyValue[1])];
            });
        }
        else {
            entries = Object.entries(d);
        }
        return new Map(entries);
    }
}
// tslint:disable-next-line:no-shadowed-variable
const Property = function Property(options = {}) {
    const { defaultValue, type, keyValue } = options;
    return function (target, propertyKey) {
        const reflected = Reflect.getMetadata('design:type', target, propertyKey);
        const isRepeated = type && reflected === Array;
        const propertyType = type ? type : reflected;
        const prop = new PropertyType(propertyType, propertyKey, isRepeated, defaultValue, keyValue);
        if (type) {
            Reflect.defineMetadata('core:type', type, target, propertyKey);
        }
        if (keyValue) {
            Reflect.defineMetadata('core:keyValue', keyValue, target, propertyKey);
        }
        definePropertyOnModel(target, propertyKey, prop);
    };
};

export { BaseModel, Builder, Data, Field, FieldType, Model, ModelAnnotation, PopulateMode, Property, PropertyType, Value };
